import React, { useState } from "react";
import styled from "styled-components";
import emailjs from "emailjs-com";

const PageHeader = styled.section`
  height: 300px;
  background: linear-gradient(rgba(255, 102, 0, 0.85), rgba(204, 82, 0, 0.85)), url("/images/oth_hdr_bg.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  h1 {
    color: white;
    font-size: 3rem;
    text-align: center;
    width: 100%;
  }

  @media (max-width: 768px) {
    height: 200px;
    h1 {
      font-size: 2rem;
    }
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: #0a0a0a;

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
`;

const ContactInfo = styled.div`
  .info-card {
    background: #1a1a1a;
    padding: 2rem;
    border-radius: 10px;
    margin-bottom: 2rem;
    border: 1px solid #333;

    h3 {
      color: #ff6600;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      img {
        width: 24px;
        height: 24px;
      }
    }

    .contact-item {
      margin-bottom: 1rem;

      .label {
        font-weight: 600;
        color: #e0e0e0;
        margin-bottom: 0.5rem;
      }

      .value {
        color: #b0b0b0;

        a {
          color: #ff6600;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }

  .map-container {
    background: #1a1a1a;
    padding: 2rem;
    border-radius: 10px;
    text-align: center;
    border: 1px solid #333;

    h3 {
      color: #ff6600;
      margin-bottom: 1rem;
    }

    .map-placeholder {
      width: 100%;
      height: 300px;
      background: #e9ecef;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      font-style: italic;
    }
  }
`;

const ContactForm = styled.div`
  background: #f8f9fa;
  padding: 3rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  h2 {
    color: #ff6600;
    margin-bottom: 2rem;
    text-align: center;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;

    label {
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: #333;
    }

    input,
    select,
    textarea {
      padding: 12px 15px;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      font-size: 16px;
      transition: border-color 0.3s ease;
      font-family: inherit;

      &:focus {
        outline: none;
        border-color: #ff6600;
        box-shadow: 0 0 0 3px rgba(255, 102, 0, 0.08);
      }

      &.error {
        border-color: #cc5200;
      }
    }

    textarea {
      resize: vertical;
      min-height: 120px;
    }

    .error-message {
      color: #cc5200;
      font-size: 14px;
      margin-top: 0.5rem;
    }
  }

  .submit-btn {
    background: #ff6600;
    color: white;
    border: none;
    padding: 15px 30px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 1px;

    &:hover:not(:disabled) {
      background: #cc5200;
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(204, 82, 0, 0.3);
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        to_email: "contact@roadwisetyres.com.au",
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      };

      const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const USER_ID = process.env.REACT_APP_EMAILJS_USER_ID || process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (SERVICE_ID && TEMPLATE_ID && USER_ID) {
        await emailjs.send(SERVICE_ID, TEMPLATE_ID, payload, USER_ID);
      } else {
        console.log("EmailJS not configured — payload:", payload);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to send message. Please try again or contact us directly.");
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <PageHeader>
        <div className="container">
          <h1>Contact Us — Fleet & Heavy Vehicle Enquiries</h1>
        </div>
      </PageHeader>

      <ContentSection>
        <div className="container">
          <div className="content-grid">
            <div>
              <ContactInfo>
                <div className="info-card">
                  <h3>
                    <img
                      src="/images/locat_icon.png"
                      alt="Location"
                    />
                    Our Location
                  </h3>
                  {/* Address removed per request - no address for now */}
                </div>

                <div className="info-card">
                  <h3>
                    <img
                      src="/images/phnee_icon.png"
                      alt="Phone"
                    />
                    Call Us
                  </h3>
                  <div className="contact-item">
                    <div className="label">Phone:</div>
                    <div className="value">
                      <a href="tel:0481313403">0481 313 403</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <div className="label">Available:</div>
                    <div className="value">24/7 for Emergency Services</div>
                  </div>
                </div>

                <div className="info-card">
                  <h3>
                    <img
                      src="/images/e-mail_icon.png"
                      alt="Email"
                    />
                    Email Us
                  </h3>
                  <div className="contact-item">
                    <div className="label">Email:</div>
                    <div className="value">
                      <a href="mailto:contact@roadwisetyres.com.au">contact@roadwisetyres.com.au</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <div className="label">Response Time:</div>
                    <div className="value">Within 24 hours</div>
                  </div>
                </div>

                <div className="map-container">
                  <h3>Find Us</h3>
                  <div className="map-placeholder">
                    Interactive Map Coming Soon
                    <br />
                    Unit 6/1717 Ipswich Road, Rocklea QLD 4106
                  </div>
                </div>
              </ContactInfo>
            </div>

            <div>
              <ContactForm>
                {isSubmitted ? (
                  <div>
                    <SuccessMessage>
                      <h3>Thank you for contacting us!</h3>
                      <p>We have received your message and will get back to you within 24 hours.</p>
                      <button
                        className="btn"
                        onClick={() => setIsSubmitted(false)}
                        style={{ marginTop: "1rem" }}>
                        Send Another Message
                      </button>
                    </SuccessMessage>
                  </div>
                ) : (
                  <>
                    <h2>Send Us a Message</h2>
                    <form onSubmit={handleSubmit}>
                      <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={errors.name ? "error" : ""}
                          placeholder="Enter your full name"
                        />
                        {errors.name && <span className="error-message">{errors.name}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={errors.email ? "error" : ""}
                          placeholder="Enter your email address"
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={errors.phone ? "error" : ""}
                          placeholder="Enter your phone number"
                        />
                        {errors.phone && <span className="error-message">{errors.phone}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="subject">Subject *</label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className={errors.subject ? "error" : ""}>
                          <option value="">Select a subject</option>
                          <option value="General Inquiry">General Inquiry</option>
                          <option value="Quote Request">Quote Request (Fleet / Truck)</option>
                          <option value="Service Booking">Service Booking (Depot or On-Road)</option>
                          <option value="Emergency Service">Emergency Service (Truck/Roadtrain)</option>
                          <option value="Complaint">Complaint</option>
                          <option value="Feedback">Feedback</option>
                        </select>
                        {errors.subject && <span className="error-message">{errors.subject}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">Message *</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          className={errors.message ? "error" : ""}
                          placeholder="Please enter your message here..."
                        />
                        {errors.message && <span className="error-message">{errors.message}</span>}
                      </div>

                      <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}>
                        {isSubmitting ? "Sending Message..." : "Send Message"}
                      </button>
                    </form>
                  </>
                )}
              </ContactForm>
            </div>
          </div>
        </div>
      </ContentSection>
    </>
  );
};

export default Contact;
